import * as THREE from "three";
import {
  CHAR_RIM_COLOR,
  CHAR_RIM_DEPTH,
  CHAR_RIM_HOVER,
  INTERACT_RIM_HOVER_UV,
  INTERACT_RIM_UV,
  faceCameraYaw,
  facingFlipSign,
  setSilhouetteColor,
  setSilhouetteMap,
  silhouetteMaterial,
  SPRITE_FOOT_Y,
} from "./WorldArt";

const BILLBOARD_SCALE = 1;
/** Permanent cream hull - thinner than a loud hover inflate. */
const MESH_SCALE = 1.065;
const MESH_HOVER_SCALE = 1.095;

/**
 * Cream silhouette outline around interactables (same style as pup rims, slightly
 * thinner). Permanent by default; set `permanentRim: false` when the sprite already
 * bakes a cream stroke (player-style dog art).
 */
export class HoverHighlight {
  readonly group = new THREE.Group();
  private readonly billboard: THREE.Mesh | null = null;
  private readonly meshOutlines: THREE.Mesh[] = [];
  private readonly meshMat: THREE.MeshBasicMaterial | null = null;
  private readonly permanentRim: boolean;
  private active = false;
  private facing = Math.PI / 2;

  constructor(opts?: {
    billboard?: { width: number; height: number; map?: THREE.Texture | null };
    /** Clone a soft outline hull around each solid mesh in this object. */
    meshSource?: THREE.Object3D;
    facing?: number;
    /**
     * Keep the cream rim always on. False = hover-only (use when the texture
     * already has a baked pup-style outline).
     */
    permanentRim?: boolean;
  }) {
    this.facing = opts?.facing ?? Math.PI / 2;
    this.permanentRim = opts?.permanentRim !== false;

    if (opts?.billboard) {
      const { width, height, map } = opts.billboard;
      this.billboard = new THREE.Mesh(
        new THREE.PlaneGeometry(width, height),
        map
          ? silhouetteMaterial(map, CHAR_RIM_COLOR, INTERACT_RIM_UV)
          : new THREE.MeshBasicMaterial({
              color: CHAR_RIM_COLOR,
              transparent: true,
              opacity: 0.4,
              depthWrite: false,
              side: THREE.DoubleSide,
            }),
      );
      this.billboard.position.set(0, height * SPRITE_FOOT_Y, -CHAR_RIM_DEPTH);
      this.billboard.scale.setScalar(BILLBOARD_SCALE);
      this.billboard.visible = this.permanentRim;
      this.billboard.renderOrder = 1;
      this.billboard.userData.charRim = true;
      this.group.add(this.billboard);
    }

    if (opts?.meshSource) {
      this.meshMat = this.buildMeshOutlines(opts.meshSource);
    }
  }

  private buildMeshOutlines(source: THREE.Object3D): THREE.MeshBasicMaterial {
    const mat = new THREE.MeshBasicMaterial({
      color: CHAR_RIM_COLOR,
      side: THREE.BackSide,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
    });
    mat.userData.hoverOutline = true;

    source.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) {
        return;
      }
      if (
        child.userData.pickVolume ||
        child.userData.hoverOutline ||
        child.userData.charRim ||
        child.userData.portalPulse ||
        child.userData.travelPortal ||
        child.userData.skipHoverOutline
      ) {
        return;
      }
      const srcMat = child.material;
      if (Array.isArray(srcMat)) {
        return;
      }
      if (
        srcMat instanceof THREE.MeshBasicMaterial &&
        srcMat.transparent &&
        (srcMat.opacity < 0.35 || srcMat.depthWrite === false) &&
        !srcMat.map
      ) {
        return;
      }

      const outline = new THREE.Mesh(child.geometry, mat);
      outline.scale.setScalar(MESH_SCALE);
      outline.visible = this.permanentRim;
      outline.renderOrder = (child.renderOrder || 0) - 1;
      outline.userData.hoverOutline = true;
      outline.userData.charRim = true;
      child.add(outline);
      this.meshOutlines.push(outline);
    });
    return mat;
  }

  setMap(map: THREE.Texture | null): void {
    if (!this.billboard || !map) {
      return;
    }
    setSilhouetteMap(this.billboard, map);
  }

  setFacing(facing: number): void {
    this.facing = facing;
  }

  setActive(on: boolean): void {
    this.active = on;
    if (this.billboard) {
      this.billboard.visible = this.permanentRim || on;
      setSilhouetteColor(this.billboard, on ? CHAR_RIM_HOVER : CHAR_RIM_COLOR);
      const mat = this.billboard.material;
      if (mat instanceof THREE.ShaderMaterial && mat.uniforms.dilate) {
        mat.uniforms.dilate.value = Math.min(
          0.02,
          Math.max(0.006, on ? INTERACT_RIM_HOVER_UV : INTERACT_RIM_UV),
        );
      }
    }
    if (this.meshMat) {
      this.meshMat.color.setHex(on ? CHAR_RIM_HOVER : CHAR_RIM_COLOR);
    }
    for (const mesh of this.meshOutlines) {
      mesh.visible = this.permanentRim || on;
    }
  }

  get isActive(): boolean {
    return this.active;
  }

  update(time: number, camera: THREE.Camera): void {
    if (!this.permanentRim && !this.active) {
      return;
    }
    const pulse = this.active ? 1 + Math.sin(time * 0.004) * 0.03 : 1;
    if (this.meshOutlines.length > 0) {
      const scale = (this.active ? MESH_HOVER_SCALE : MESH_SCALE) * pulse;
      for (const mesh of this.meshOutlines) {
        mesh.scale.setScalar(scale);
      }
    }
    if (!this.billboard || !this.billboard.visible) {
      return;
    }
    const boardPulse = BILLBOARD_SCALE * pulse;
    faceCameraYaw(this.billboard, camera);
    const world = this.group.getWorldPosition(new THREE.Vector3());
    const flip = facingFlipSign(world.x, world.z, this.facing, camera);
    this.billboard.scale.set(flip * boardPulse, boardPulse, 1);
  }

  destroy(): void {
    if (this.billboard) {
      this.billboard.geometry.dispose();
      (this.billboard.material as THREE.Material).dispose();
    }
    const shared = new Set<THREE.Material>();
    for (const mesh of this.meshOutlines) {
      mesh.removeFromParent();
      const mat = mesh.material;
      if (mat instanceof THREE.Material) {
        shared.add(mat);
      }
    }
    for (const mat of shared) {
      mat.dispose();
    }
    this.meshOutlines.length = 0;
    this.group.removeFromParent();
  }
}

/** Invisible pick volume for raycasts. */
export function makePickVolume(radius: number, height: number): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.CylinderGeometry(radius, radius, height, 10),
    new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthWrite: false,
    }),
  );
  mesh.position.y = height * 0.5;
  mesh.userData.pickVolume = true;
  return mesh;
}
